/**
 * Confirmer un ayant droit sur pièce justificative — décision du Tech Lead
 * du 28/09.
 *
 * Jusqu'ici, seule la chargée de clientèle confirmait un conjoint ou un
 * enfant enrôlé au guichet, contre le DNS papier de l'entreprise. Mais un DNS
 * ne nomme jamais un ayant droit (CP-01) : il en donne le NOMBRE. Ce que la
 * chargée vérifiait, c'est donc que le nombre tient, et que le lien est
 * prouvé. L'agent d'accueil, qui a l'acte de mariage ou le livret de famille
 * sous les yeux, peut le faire lui-même — tant que le compte tombe juste.
 *
 * Au-delà de ce que le salarié a déclaré au DNS, la décision revient à la
 * chargée : garder les deux, ou retirer l'ancien. Ce n'est pas au comptoir de
 * trancher qui, d'un premier et d'un second conjoint, est encore couvert.
 *
 * Pure et partagée : l'API refuse avec ces motifs, l'écran grise le bouton
 * avec les mêmes, dans les mêmes mots.
 */
import { formatDeScan } from '../dns/detection.js';
/** Les deux liens qu'une pièce peut prouver. */
export const LIENS_AYANT_DROIT = ['CONJOINT', 'ENFANT'];
export function estLienAyantDroit(type) {
    return LIENS_AYANT_DROIT.includes(type);
}
/**
 * Les pièces qu'AFS peut accepter pour chaque lien, et ce qu'elle accepte
 * tant que la Direction n'a rien changé : les pièces officielles.
 *
 * Un acte de naissance ne prouve pas un mariage, un acte de mariage ne prouve
 * pas une filiation : ces deux couples ne sont pas proposés du tout — une case
 * à cocher qui n'a pas de sens pour la Direction serait une case de trop.
 * « Autre » est proposé, jamais accepté d'office.
 */
export const PIECES_PAR_LIEN = {
    CONJOINT: [
        { piece: 'ACTE_MARIAGE', parDefaut: true },
        { piece: 'LIVRET_FAMILLE', parDefaut: true },
        { piece: 'AUTRE', parDefaut: false },
    ],
    ENFANT: [
        { piece: 'ACTE_NAISSANCE', parDefaut: true },
        { piece: 'LIVRET_FAMILLE', parDefaut: true },
        { piece: 'AUTRE', parDefaut: false },
    ],
};
export const PIECES_ACCEPTEES_PAR_DEFAUT = {
    CONJOINT: PIECES_PAR_LIEN.CONJOINT.filter((p) => p.parDefaut).map((p) => p.piece),
    ENFANT: PIECES_PAR_LIEN.ENFANT.filter((p) => p.parDefaut).map((p) => p.piece),
};
export const LIBELLE_PIECE = {
    ACTE_MARIAGE: 'Acte de mariage',
    ACTE_NAISSANCE: 'Copie d’acte de naissance',
    LIVRET_FAMILLE: 'Livret de famille',
    AUTRE: 'Autre pièce',
};
/**
 * Retour du terrain du 04/11 (S1429) — une pièce « Autre » dit ce qu'elle est,
 * en quelques mots : la chargée doit savoir quoi regarder sans ouvrir le
 * fichier. Les autres natures se suffisent : aucune précision ne s'y garde.
 */
export const PRECISION_PIECE_MAX = 80;
export const PRECISION_PIECE_OBLIGATOIRE = 'Précisez ce qu’est la pièce : la nature « Autre » le demande.';
/** La précision gardée : nettoyée pour « Autre », toujours `null` pour une autre nature. */
export function precisionDePiece(nature, brute) {
    if (nature !== 'AUTRE')
        return null;
    const nette = (brute ?? '').replace(/\s+/g, ' ').trim();
    return nette === '' ? null : nette;
}
/** « Autre pièce : attestation de vie commune », ou le libellé seul. */
export function libelleNaturePiece(nature, precision) {
    return nature === 'AUTRE' && precision !== null
        ? `${LIBELLE_PIECE.AUTRE} : ${precision}`
        : LIBELLE_PIECE[nature];
}
/**
 * Le salarié a-t-il déclaré assez d'ayants droit de ce lien pour un de plus ?
 * `autres` compte ceux du même lien déjà rattachés, actifs et non infirmés —
 * la personne examinée exclue.
 */
export function depasseLeDns(autres, declares) {
    return autres + 1 > declares;
}
/**
 * Ce qui compte face au DNS : un ayant droit actif, et pas infirmé. Un
 * « à confirmer » compte : il est servi, et c'est précisément lui que le
 * compte doit arrêter s'il est de trop. L'API en tire sa clause Prisma
 * (`COMPTE_FACE_AU_DNS`), l'écran compte avec elle : les deux comptent pareil.
 */
export function compteFaceAuDns(p) {
    return p.statut === 'ACTIF' && p.etatConfirmation !== 'INFIRME';
}
/**
 * Parmi les ayants droit d'un salarié, combien de ce lien comptent déjà face
 * au DNS. C'est ce que l'accueil lit avant de créer une fiche de plus.
 */
export function dejaDuMemeLien(ayantsDroit, lien) {
    return ayantsDroit.filter((a) => a.typeBeneficiaire === lien && compteFaceAuDns(a)).length;
}
export const MOTIFS_REFUS_SUR_PIECE = [
    'PAS_UN_AYANT_DROIT',
    'DEJA_TRAITEE',
    'TITULAIRE_NON_CONFIRME',
    'SANS_PIECE',
    'PIECE_NON_ACCEPTEE',
    'SANS_FICHIER',
];
/** Des phrases pour l'agent : il les lit sous le bouton grisé, ou dans le refus. */
const MESSAGES = {
    PAS_UN_AYANT_DROIT: 'Seul un conjoint ou un enfant se confirme sur pièce justificative.',
    DEJA_TRAITEE: 'Cette fiche n’attend plus de confirmation.',
    TITULAIRE_NON_CONFIRME: 'Le salarié de rattachement n’est pas encore confirmé : la chargée de clientèle ' +
        'confirmera cette fiche.',
    SANS_PIECE: 'Indiquez d’abord quelle pièce a été présentée.',
    PIECE_NON_ACCEPTEE: 'Cette pièce ne suffit pas pour confirmer à l’accueil : la chargée de clientèle ' +
        'confirmera cette fiche.',
    SANS_FICHIER: 'Joignez d’abord la photo ou le scan de la pièce.',
};
function refus(motif) {
    return { possible: false, motif, message: MESSAGES[motif] };
}
/**
 * Réunion du 15/10 — avec plusieurs pièces, celle qui compte pour la
 * confirmation : la première, de la plus récente à la plus ancienne, que la
 * Configuration accepte pour ce lien (`piece`). Aucune d'acceptée : la nature
 * de la plus récente, pour que la règle dise pourquoi elle ne suffit pas.
 * Aucune pièce du tout : la nature notée sur la fiche, sans fichier.
 *
 * `pieces` va de la plus récente à la plus ancienne. Une nature retenue
 * vient toujours d'une pièce jointe quand la fiche peut être confirmée : le
 * journal peut dire laquelle (revue S03, A2).
 */
export function pieceRetenue(lien, pieces, noteeSurLaFiche, acceptees) {
    const acceptee = pieces.find((p) => acceptees[lien].includes(p.nature));
    if (acceptee !== undefined)
        return { piece: acceptee, nature: acceptee.nature };
    return { piece: null, nature: pieces[0]?.nature ?? noteeSurLaFiche };
}
/**
 * Peut-on confirmer cette fiche sur pièce, au guichet ?
 *
 * L'ordre des refus est celui dans lequel l'agent peut agir : ce qu'il ne
 * peut pas changer d'abord (le type, l'état, le salarié), puis ce qu'il peut
 * compléter (la pièce, son fichier), et en dernier le compte du DNS — qui
 * renvoie à la chargée même quand tout le reste est en ordre.
 */
export function peutConfirmerSurPiece(ctx) {
    if (!estLienAyantDroit(ctx.typeBeneficiaire))
        return refus('PAS_UN_AYANT_DROIT');
    if (ctx.etatConfirmation !== 'A_CONFIRMER')
        return refus('DEJA_TRAITEE');
    if (!ctx.titulaireConfirme)
        return refus('TITULAIRE_NON_CONFIRME');
    if (ctx.piece === null)
        return refus('SANS_PIECE');
    if (!ctx.piecesAcceptees[ctx.typeBeneficiaire].includes(ctx.piece)) {
        return refus('PIECE_NON_ACCEPTEE');
    }
    if (!ctx.fichierJoint)
        return refus('SANS_FICHIER');
    // Réunion du 15/10 : avec sa pièce, un ayant droit est accepté même
    // au-delà de ce que le salarié a déclaré au DNS. La chargée en est
    // seulement prévenue, pour retirer un ancien si besoin (voir l'API).
    return { possible: true };
}
export const TYPE_MIME_PIECE = {
    PDF: 'application/pdf',
    JPEG: 'image/jpeg',
    PNG: 'image/png',
    WEBP: 'image/webp',
};
/** Le format lu dans les octets — jamais dans l'extension ni le type annoncé. */
export function formatDePiece(octets) {
    const scan = formatDeScan(octets);
    if (scan !== null)
        return scan;
    // WebP : « RIFF », quatre octets de taille, puis « WEBP ».
    const riff = [0x52, 0x49, 0x46, 0x46].every((o, i) => octets[i] === o);
    const webp = [0x57, 0x45, 0x42, 0x50].every((o, i) => octets[8 + i] === o);
    return riff && webp ? 'WEBP' : null;
}
// ─────────────────────────────────────────────────────────────────────
//  Retirer un ayant droit confirmé — décision du Tech Lead du 29/09
// ─────────────────────────────────────────────────────────────────────
/**
 * Pourquoi la chargée de clientèle retire un conjoint ou un enfant déjà
 * confirmé. Une courte liste et jamais un texte libre : le motif part au
 * journal, et un texte libre finirait par y porter un nom.
 *
 * Reprend valeur pour valeur l'enum Prisma `MotifRetraitAyantDroit` (un test
 * API le vérifie).
 */
export const MOTIFS_RETRAIT_AYANT_DROIT = ['SEPARATION', 'DECES', 'LIMITE_AGE', 'AUTRE'];
export const LIBELLE_MOTIF_RETRAIT = {
    SEPARATION: 'Séparation ou divorce',
    DECES: 'Décès',
    LIMITE_AGE: 'Enfant au-delà de l’âge de couverture',
    AUTRE: 'Autre motif',
};
export const MOTIFS_REFUS_RETRAIT = [
    'PAS_UN_AYANT_DROIT',
    'INFIRME',
    'A_CONFIRMER',
    'DEJA_INACTIF',
];
const MESSAGES_RETRAIT = {
    PAS_UN_AYANT_DROIT: 'Seul un conjoint ou un enfant se retire de la couverture de son salarié.',
    INFIRME: 'Cette fiche est infirmée : elle n’est déjà plus couverte.',
    A_CONFIRMER: 'Cette fiche attend encore sa confirmation : infirmez-la plutôt que de la retirer.',
    DEJA_INACTIF: 'Cet ayant droit n’est déjà plus couvert.',
};
/**
 * La chargée peut-elle retirer cet ayant droit ? Seulement un conjoint ou un
 * enfant CONFIRMÉ et encore actif. Une fiche « à confirmer » ne se retire
 * pas : elle s'infirme, par la voie de toujours.
 *
 * L'API refuse avec ces messages ; l'écran n'offre le bouton que si la règle
 * le permet.
 */
export function peutRetirerAyantDroit(p) {
    const refus = (motif) => ({
        possible: false,
        motif,
        message: MESSAGES_RETRAIT[motif],
    });
    if (!estLienAyantDroit(p.typeBeneficiaire))
        return refus('PAS_UN_AYANT_DROIT');
    if (p.etatConfirmation === 'INFIRME')
        return refus('INFIRME');
    if (p.etatConfirmation === 'A_CONFIRMER')
        return refus('A_CONFIRMER');
    if (p.statut !== 'ACTIF')
        return refus('DEJA_INACTIF');
    return { possible: true };
}
/**
 * Retiré par la chargée, et pas désactivé pour une autre raison : c'est ce
 * retrait-là, et lui seul, qui s'annule. Le motif du retrait n'est posé que
 * par lui ; la désactivation manuelle est son motif d'inactivité.
 */
export function estRetireALaMain(p) {
    return (p.statut === 'INACTIF' &&
        p.motifInactivite === 'DESACTIVATION_MANUELLE' &&
        p.motifRetrait !== null);
}
//# sourceMappingURL=ayants-droit.js.map